import classNames from 'classnames/bind'
import React from 'react'
import PropTypes from 'prop-types'
import { MultipleChoice, EssayWithSubitems } from 'components'
import { ArrowButton } from '@plurall/elo'
import queryString from 'query-string'
import { Link, Redirect } from 'react-router-dom'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import { Client, uploadConfig } from 'utils'

import styles from './QuestionCreate.module.scss'

const cx = classNames.bind(styles)

class QuestionCreate extends React.Component {
  state = {
    enunciations: [{ id: new Date().getTime(), value: '' }],
    suggestions: [{ id: new Date().getTime(), value: '' }],
    teacherSuggestions: [{ id: new Date().getTime(), value: '' }],
    videoUrls: [{ id: new Date().getTime(), value: '' }],
    options: [],
    isValid: false,
    isOptionsValid: false,
    isUrlValid: true,
    redirect: '',
    loading: false,
  }

  async componentDidMount() {
    const params = queryString.parse(this.props.location.search)
    const question = JSON.parse(params.question)
    this.setState(question)
  }

  onChange = (index, editor, type) => {
    const item = this.state[type].slice()
    item[index].value = editor.getData()
    this.setState({ [type]: item })
    if (type === 'enunciations' || type === 'suggestions') this.validateFields()
  }

  onChangeVideo = (index, event) => {
    const videoUrl = this.state.videoUrls.slice()
    videoUrl[index].value = event.target.value
    const validUrl = event.target.value === '' ? true : this.isURL(event.target.value)
    videoUrl[index].valid = validUrl
    this.setState({ videoUrls: videoUrl, isUrlValid: validUrl }, () => {
      this.validateFields()
    })
  }

  isURL = str => {
    const pattern = new RegExp(
      /((http|https):\/\/|)(www\.|)(facebook|livestream|vimeo|youtube|youtu)\.(com|be)\/[a-zA-Z0-9.]{1,}/, // eslint:disable-line
    )

    return pattern.test(str)
  }

  addMore = type => {
    const newItem = [...this.state[type], { id: new Date().getTime(), value: '' }]
    this.setState({ [type]: newItem }, () => {
      this.validateFields()
    })
  }

  remove = (event, type) => {
    const array = [...this.state[type]]
    array.splice(event.target.value, 1)
    this.setState({ [type]: array }, () => {
      this.validateFields()
    })
  }

  callbackMultipleChoice = options => {
    if (options.length > 1) {
      const arrayOption = options.filter(option => option.text !== '')
      const optionCorrect = options.some(option => option.correct === true)
      const valid = options.length === arrayOption.length && optionCorrect
      this.setState({ isOptionsValid: valid, options }, () => {
        this.validateFields()
      })
    } else {
      this.setState({ isOptionsValid: false, options }, () => {
        this.validateFields()
      })
    }
  }

  callbackEssayWithSubitems = subItems => {
    const arraySubitems = subItems.options.filter(option => option.text !== '')
    const valid = subItems.options.length === arraySubitems.length

    this.setState({ isOptionsValid: valid }, () => {
      this.validateFields()
    })
    this.setState(subItems)
  }

  validateFields = () => {
    const arrayEnunciations = this.state.enunciations.filter(
      enunciation => enunciation.value !== '',
    )
    const enunciationsValid = this.state.enunciations.length === arrayEnunciations.length

    const arraySuggestions = this.state.suggestions.filter(suggestion => suggestion.value !== '')
    const suggestionsValid = this.state.suggestions.length === arraySuggestions.length

    const optionsValid =
      (this.state.question_kind === 'multiple_choice' ||
        this.state.question_kind === 'objective' ||
        this.state.question_kind === 'open_response_subitem') &&
      this.state.isOptionsValid

    let buttonValid = false

    if (
      this.state.question_kind === 'open_response' &&
      enunciationsValid &&
      suggestionsValid &&
      this.state.isUrlValid
    ) {
      buttonValid = true
    } else if (enunciationsValid && suggestionsValid && optionsValid && this.state.isUrlValid) {
      buttonValid = true
    }

    this.setState({ isValid: buttonValid })
  }

  buildLinkTarget = () => {
    const params = queryString.parse(this.props.location.search)
    const question = JSON.parse(params.question)
    return `/create?question=${JSON.stringify(question)}`
  }

  client = new Client()

  createQuestion = () => {
    this.setState({ loading: true })
    const post = {
      discipline_ids: this.state.disciplinesIds,
      difficulty_level: this.state.difficulty,
      tags: this.state.tags && this.state.tags.split(','),
      number: this.state.number,
      question_kind:
        this.state.question_kind === 'objective' ? 'multiple_choice' : this.state.question_kind,
      texts: this.state.enunciations.map(
        enunciation => `${window.WirisPlugin.Parser.initParse(enunciation.value)} <p></p><p></p>`,
      ),
      answers: this.state.suggestions.map(
        suggestion => `${window.WirisPlugin.Parser.initParse(suggestion.value)} <p></p><p></p>`,
      ),
      teacher_answers:
        this.state.teacherSuggestions &&
        this.state.teacherSuggestions
          .filter(teacherSuggestion => teacherSuggestion.value !== '')
          .map(
            teacherSuggestion =>
              `${window.WirisPlugin.Parser.initParse(teacherSuggestion.value)} <p></p><p></p>`,
          ),
      answers_videos:
        this.state.videoUrls &&
        this.state.videoUrls
          .filter(videoUrl => videoUrl.value !== '')
          .map(videoUrl => ({
            url: videoUrl.value,
            description: '',
          })),
    }
    if (
      this.state.question_kind === 'objective' ||
      this.state.question_kind === 'multiple_choice'
    ) {
      post.options = this.state.options.map((option, index) => ({
        correct: option.correct ?? false,
        text: window.WirisPlugin.Parser.initParse(option.text),
        sequence: String.fromCharCode(65 + index).toUpperCase(),
      }))
    } else if (this.state.question_kind === 'open_response_subitem') {
      post.open_response_subitems = this.state.options.map(option => ({
        text: window.WirisPlugin.Parser.initParse(option.text),
      }))
    }

    Object.keys(post).forEach(
      key =>
        (post[key] === '' || post[key] === undefined || post[key].length === 0) && delete post[key],
    )
    this.handleCreateQuestion(post)
  }

  handleCreateQuestion = async post => {
    const { question, code, msg } = await this.client.createQuestion(post)
    let message = {
      message: '',
      status: '',
    }
    if (question && question.id) {
      message = {
        status: 'success',
        message: `Questão criada com sucesso. Node: ${question.id}`,
      }
    } else {
      message = { status: code === 500 && 'error', message: msg }
    }
    const redirectTo = `message=${message.message}&status=${message.status}`
    this.setState({ redirect: redirectTo, loading: false })
  }

  render() {
    const borderClass = cx({ 'border-bottom': this.state.question_kind !== 'open_response' })
    return (
      <>
        <div className={styles.header}>
          <Link to={this.buildLinkTarget}>
            <ArrowButton direction='left' />
          </Link>
          <h1 className={styles.heading}>
            Questão
            {this.state.question_kind === 'multiple_choice' && ' múltipla escolha'}
            {this.state.question_kind === 'open_response_subitem' && ' dissertativa com subitens'}
            {this.state.question_kind === 'objective' && ' objetiva'}
            {this.state.question_kind === 'open_response' && ' discursiva'}
          </h1>
        </div>
        <form className={styles['question-create']}>
          <div className={borderClass}>
            {this.state.enunciations.map((enunciation, index) => (
              <div key={enunciation.id}>
                <label className={styles.display}>Enunciado </label>
                <div className={styles.ckeditor}>
                  <CKEditor
                    editor={window.ClassicEditor}
                    config={uploadConfig}
                    onChange={(_, editor) => this.onChange(index, editor, 'enunciations')}
                  />
                </div>
                {this.state.enunciations.length > 1 && (
                  <div className={styles['div-button']}>
                    <button
                      className={styles['button-delete']}
                      type='button'
                      value={index}
                      onClick={event => this.remove(event, 'enunciations')}
                    >
                      Remover
                    </button>
                  </div>
                )}
              </div>
            ))}
            <button
              type='button'
              className={styles['button-add']}
              onClick={() => this.addMore('enunciations')}
            >
              + item
            </button>
          </div>

          {(this.state.question_kind === 'multiple_choice' ||
            this.state.question_kind === 'objective') && (
            <MultipleChoice
              callback={this.callbackMultipleChoice}
              type={this.state.question_kind}
            />
          )}
          {this.state.question_kind === 'open_response_subitem' && (
            <EssayWithSubitems callback={this.callbackEssayWithSubitems} />
          )}

          {this.state.suggestions.map((suggestion, index) => (
            <div key={suggestion.id} className={styles.border}>
              <label className={styles.display}> Gabarito comentado </label>
              <div className={styles.ckeditor}>
                <CKEditor
                  editor={window.ClassicEditor}
                  config={uploadConfig}
                  onChange={(_, editor) => this.onChange(index, editor, 'suggestions')}
                />
              </div>
              {this.state.suggestions.length > 1 && (
                <div className={styles['div-button']}>
                  <button
                    className={styles['button-delete']}
                    type='button'
                    value={index}
                    onClick={event => this.remove(event, 'suggestions')}
                  >
                    Remover
                  </button>
                </div>
              )}
            </div>
          ))}
          <button
            type='button'
            className={styles['button-add']}
            onClick={() => this.addMore('suggestions')}
          >
            + item
          </button>

          {this.state.teacherSuggestions.map((teacherSuggestion, index) => (
            <div key={teacherSuggestion.id} className={styles.border}>
              <label className={styles.display}> Gabarito professor </label>
              <div className={styles.ckeditor}>
                <CKEditor
                  editor={window.ClassicEditor}
                  config={uploadConfig}
                  onChange={(_, editor) => this.onChange(index, editor, 'teacherSuggestions')}
                />
              </div>
              {this.state.teacherSuggestions.length > 1 && (
                <div className={styles['div-button']}>
                  <button
                    className={styles['button-delete']}
                    type='button'
                    value={index}
                    onClick={event => this.remove(event, 'teacherSuggestions')}
                  >
                    Remover
                  </button>
                </div>
              )}
            </div>
          ))}
          <button
            type='button'
            className={styles['button-add']}
            onClick={() => this.addMore('teacherSuggestions')}
          >
            + item
          </button>

          {this.state.videoUrls.map((videoUrl, index) => (
            <div key={videoUrl.id} className={styles.border}>
              <label className={styles.display}> Gabarito em vídeo </label>
              <input
                type='text'
                name='videoFeedback'
                placeholder='URL'
                className={styles.url}
                onChange={event => this.onChangeVideo(index, event)}
              />
              {videoUrl.value && !videoUrl.valid && (
                <p className={styles.error}>
                  O link do vídeo deve ser uma url válida do youtube, vimeo, facebook ou livestream.
                </p>
              )}
              {this.state.videoUrls.length > 1 && (
                <div className={styles['div-button-video']}>
                  <button
                    className={styles['button-delete']}
                    type='button'
                    value={index}
                    onClick={event => this.remove(event, 'videoUrls')}
                  >
                    Remover
                  </button>
                </div>
              )}
            </div>
          ))}
          <button
            type='button'
            className={styles['button-add']}
            onClick={() => this.addMore('videoUrls')}
          >
            + item
          </button>

          <div className={styles['div-button']}>
            <input
              className={styles.button}
              type='button'
              value={`${this.state.loading ? 'Salvando' : 'Salvar'}`}
              onClick={this.createQuestion}
              disabled={!this.state.isValid || this.state.loading}
            />
          </div>
        </form>
        {this.state.redirect && (
          <Redirect
            to={{
              pathname: '/create',
              search: this.state.redirect,
            }}
          />
        )}
      </>
    )
  }
}
QuestionCreate.propTypes = {
  location: PropTypes.object,
}
export default QuestionCreate
